import { useEffect, useState, useRef } from "react";
import { Search, Plus, Users, FileText, Edit2, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import api from "../services/api";
import ClientForm from "../components/ClientForm";
import Swal from "sweetalert2";

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [editingClient, setEditingClient] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const searchRef = useRef(null);

  // PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [from, setFrom] = useState(0);
  const [to, setTo] = useState(0);

  const fetchClients = async (page = 1, searchTerm = search) => {
    try {
      setLoading(true);
      const res = await api.get(`/clients/paginated?page=${page}&search=${searchTerm}`);

      if (res.data.data) {
        setClients(res.data.data);
        setCurrentPage(res.data.current_page);
        setTotalPages(res.data.last_page);
        setTotal(res.data.total);
        setFrom(res.data.from);
        setTo(res.data.to);
      } else {
        setClients(Array.isArray(res.data) ? res.data : res.data.data || []);
      }
    } catch (error) {
      console.error("Error loading clients:", error);
      Swal.fire("Error", "Could not load clients", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    setTimeout(() => {
      searchRef.current?.focus();
    }, 100);
  }, []);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchClients(newPage, search);
    }
  };

  const handleSearch = (value) => {
    setSearch(value);
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      setCurrentPage(1);
      fetchClients(1, value);
    }, 500);
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Delete client?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await api.delete(`/clients/${id}`);
      Swal.fire({
        icon: "success",
        title: "Client deleted",
        text: "The client was deleted successfully",
        confirmButtonText: "OK",
      });
      fetchClients(currentPage, search);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Could not delete the client",
      });
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto" }}>
      <div className="card">
        <div className="card-header">
          <div className="d-flex align-items-center gap-2">
            <Users size={28} color="#667eea" />
            <h1 className="card-title">Client Management</h1>
          </div>

          {!showForm && (
            <button
              className="btn btn-success"
              onClick={() => {
                setEditingClient(null);
                setShowForm(true);
              }}
            >
              <Plus size={18} />
              New Client
            </button>
          )}
        </div>

        <div className="card-body">
          {showForm && (
            <ClientForm
              editingClient={editingClient}
              setEditingClient={setEditingClient}
              fetchClients={() => fetchClients(currentPage, search)}
              onCancel={() => {
                setEditingClient(null);
                setShowForm(false);
                setTimeout(() => searchRef.current?.focus(), 50);
              }}
            />
          )}

          {!showForm && (
            <div className="form-group" style={{ position: "relative" }}>
              <div style={{ position: "relative" }}>
                <Search
                  size={18}
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#a0aec0",
                  }}
                />
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search by name, email or phone..."
                  value={search}
                  onChange={(e) => handleSearch(e.target.value)}
                  ref={searchRef}
                  style={{ paddingLeft: "44px" }}
                />
              </div>
              {search && (
                <small style={{ color: "#718096", fontSize: "12px", marginTop: "4px", display: "block" }}>
                  🔍 Searching all clients...
                </small>
              )}
            </div>
          )}

          {!showForm && (
            <>
              {loading ? (
                <div className="text-center" style={{ padding: "40px" }}>
                  <div className="spinner" style={{ margin: "0 auto" }}></div>
                  <p style={{ marginTop: "16px", color: "#718096" }}>
                    Loading clients...
                  </p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th style={{ textAlign: "center", width: "180px" }}>
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {clients.length === 0 ? (
                        <tr>
                          <td colSpan="4">
                            <div
                              className="text-center"
                              style={{ padding: "40px 20px" }}
                            >
                              <FileText
                                size={48}
                                color="#cbd5e0"
                                style={{ marginBottom: "16px" }}
                              />
                              <p
                                style={{
                                  color: "#a0aec0",
                                  fontSize: "16px",
                                  margin: 0,
                                }}
                              >
                                {search
                                  ? "No clients found matching your search"
                                  : "No clients registered yet"}
                              </p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        clients.map((client) => (
                          <tr key={client.id}>
                            <td>
                              <span style={{ fontWeight: 600 }}>
                                {client.nombre}
                              </span>
                            </td>
                            <td>{client.email || "-"}</td>
                            <td>{client.telefono || "-"}</td>
                            <td>
                              <div
                                className="d-flex gap-2"
                                style={{ justifyContent: "center" }}
                              >
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => {
                                    setEditingClient(client);
                                    setShowForm(true);
                                  }}
                                  title="Edit client"
                                >
                                  <Edit2 size={16} />
                                </button>
                                <button
                                  className="btn btn-danger btn-sm"
                                  onClick={() => handleDelete(client.id)}
                                  title="Delete client"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}

              {totalPages > 1 && (
                <div
                  style={{
                    marginTop: "24px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                  </button>

                  <span style={{ padding: "6px 12px", color: "#718096", fontSize: "14px" }}>
                    Page {currentPage} of {totalPages}
                  </span>

                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>
              )}

              {clients.length > 0 && (
                <div
                  style={{
                    marginTop: "16px",
                    padding: "16px",
                    background: "#f7fafc",
                    borderRadius: "10px",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ color: "#718096", fontSize: "14px" }}>
                    Showing {from} - {to} of {total} clients
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}